/**
 * useLiveSettings Hook
 *
 * Provides live-updating settings with dirty-state awareness.
 * Subscribes to cross-tab/cross-device changes via SyncCoordinatorService.
 *
 * Behavior:
 * - When NOT dirty: applies incoming updates immediately (full replace)
 * - When dirty: queues update flag, applies after dirty clears
 * - Handles error states for fetch failures
 * - Re-subscribes if record ID changes (edge case)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';

/**
 * Hook parameters
 */
export interface UseLiveSettingsOptions<T> {
  /** IndexedDB table name (e.g., 'pre_trading_settings') */
  table: string;
  /** Function to fetch settings - must return record with id field */
  fetchSettings: () => Promise<T & { id: string }>;
  /** Whether user is currently editing (debounce active, modal open, etc.) */
  isDirty: boolean;
  /** Callback fired when an external update is applied (for toast notifications) */
  onExternalUpdate?: () => void;
}

/**
 * Hook return value
 */
export interface UseLiveSettingsResult<T> {
  /** Current settings or null if loading/error */
  settings: T | null;
  /** Update settings optimistically (also clears hasQueuedUpdate) */
  setSettings: (settings: T) => void;
  /** True during initial load */
  isLoading: boolean;
  /** True if an update arrived while dirty (will apply when dirty clears) */
  hasQueuedUpdate: boolean;
  /** Error message if fetch failed, null otherwise */
  error: string | null;
}

/**
 * Live settings hook with dirty-state awareness
 *
 * @example
 * ```tsx
 * const { settings, isLoading, hasQueuedUpdate, error } = useLiveSettings({
 *   table: 'pre_trading_settings',
 *   fetchSettings: () => preTradingSettingsService.getCurrentSettings(),
 *   isDirty: isEditing,
 * });
 * ```
 */
export function useLiveSettings<T>({
  table,
  fetchSettings,
  isDirty,
  onExternalUpdate,
}: UseLiveSettingsOptions<T>): UseLiveSettingsResult<T> {
  // State
  const [settings, setSettingsInternal] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasQueuedUpdate, setHasQueuedUpdate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordId, setRecordId] = useState<string | null>(null);

  // Refs for stable callback references
  const isDirtyRef = useRef(isDirty);
  const fetchSettingsRef = useRef(fetchSettings);
  const onExternalUpdateRef = useRef(onExternalUpdate);
  const mountedRef = useRef(true);

  // Sync refs during render (not in useEffect) to eliminate race window
  // This ensures callbacks always see current values
  isDirtyRef.current = isDirty;
  fetchSettingsRef.current = fetchSettings;
  onExternalUpdateRef.current = onExternalUpdate;

  /**
   * Exposed setSettings for optimistic updates
   * Also clears hasQueuedUpdate since optimistic update supersedes queued external update
   */
  const setSettings = useCallback((newSettings: T) => {
    setSettingsInternal(newSettings);
    setHasQueuedUpdate(false);
  }, []);

  /**
   * Fetch and apply settings
   * Used for initial load, immediate updates, and queued apply
   * Returns true on success, false on failure
   */
  const refetchAndApply = useCallback(async (): Promise<boolean> => {
    try {
      const freshSettings = await fetchSettingsRef.current();

      // Guard against post-unmount state updates
      if (!mountedRef.current) return false;

      setSettingsInternal(freshSettings);
      setError(null);
      setHasQueuedUpdate(false); // Clear on any successful apply

      // Check if record ID changed (edge case: new settings record created)
      if (freshSettings.id !== recordId) {
        setRecordId(freshSettings.id);
      }

      return true;
    } catch (err) {
      // Guard against post-unmount state updates
      if (!mountedRef.current) return false;

      console.error('[useLiveSettings] Fetch failed:', err);
      setError('Failed to load settings');
      return false;
    }
  }, [recordId]);

  // Initial load on mount
  useEffect(() => {
    mountedRef.current = true;

    const loadInitial = async () => {
      setIsLoading(true);
      try {
        const initialSettings = await fetchSettingsRef.current();

        // Guard against post-unmount state updates
        if (!mountedRef.current) return;

        setSettingsInternal(initialSettings);
        setRecordId(initialSettings.id);
        setError(null);
      } catch (err) {
        // Guard against post-unmount state updates
        if (!mountedRef.current) return;

        console.error('[useLiveSettings] Initial load failed:', err);
        setError('Failed to load settings');
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadInitial();

    // Cleanup: mark as unmounted
    return () => {
      mountedRef.current = false;
    };
  }, []); // Only run on mount

  // Subscribe to cross-tab changes after initial load
  useEffect(() => {
    // Don't subscribe until we have a record ID
    if (!recordId) return;

    const unsubscribe = syncCoordinatorService.subscribeByRecord(
      table,
      recordId,
      async () => {
        // Read current dirty state from ref (not stale closure)
        if (isDirtyRef.current) {
          // User is editing - queue the update, don't apply yet
          setHasQueuedUpdate(true);
        } else {
          // Not editing - apply update immediately
          const success = await refetchAndApply();
          if (success) {
            // Notify parent of external update (for toast)
            onExternalUpdateRef.current?.();
          } else {
            // Track failed apply so UI can indicate pending update
            setHasQueuedUpdate(true);
          }
        }
      }
    );

    return unsubscribe;
  }, [table, recordId, refetchAndApply]);

  // Watch isDirty transition: when it goes from true to false, apply queued update
  const prevIsDirtyRef = useRef(isDirty);

  useEffect(() => {
    const wasEditing = prevIsDirtyRef.current;
    const isNowEditing = isDirty;
    prevIsDirtyRef.current = isDirty;

    // Detect transition: was editing, now not editing
    if (wasEditing && !isNowEditing && hasQueuedUpdate) {
      // Apply the queued update
      // Note: refetchAndApply clears hasQueuedUpdate on success, keeps it on failure
      const applyQueuedUpdate = async () => {
        const success = await refetchAndApply();
        if (success) {
          // Notify parent of external update (for toast)
          onExternalUpdateRef.current?.();
        }
      };
      applyQueuedUpdate();
    }
  }, [isDirty, hasQueuedUpdate, refetchAndApply]);

  return {
    settings,
    setSettings,
    isLoading,
    hasQueuedUpdate,
    error,
  };
}
